import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  ColorPicker,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { ClearOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { PageHeader } from '@shared/components/PageHeader';
import {
  AdminTenantConfigDto,
  AntifraudConfig,
  PartnerTierLayoutItem,
  ShopTaxonomy,
  UpdateConfigInput,
  useTenantAdminConfig,
  useUpdateTenantConfig,
} from '@shared/api/hooks/useTenantAdminConfig';
import { PurgeImagesResult, usePurgeReceiptImages } from '@shared/api/hooks/useReceipts';
import {
  ANTIFRAUD_BALANCED_DEFAULT,
  AntifraudSettingsTab,
  validateAntifraudConfig,
} from './AntifraudSettingsTab';
import {
  SHOP_TAXONOMY_DEFAULT,
  ShopTaxonomyTab,
  normalizeShopTaxonomy,
  validateShopTaxonomy,
} from './ShopTaxonomyTab';

interface MetadataDraft {
  storeUrl?: { ios?: string; android?: string };
  forceUpdateBelow?: string;
  [k: string]: unknown;
}

interface FormValues {
  // Branding
  primaryColor: string;
  secondaryColor: string;
  accentColor?: string;
  logoUrl?: string;
  // Club
  clubName: string;
  appName?: string;
  clubCity?: string;
  clubStadium?: string;
  clubFounded?: number;
  // Check-in stadio: gestito ora SOLO in Loyalty > Check in (T-267).
  // App & shop
  defaultLocale: string;
  timezone: string;
  minAppVersion: string;
  subscriberDiscountPct: number;
  subscriptionEndsAt?: dayjs.Dayjs | null;
  hideNextMatch: boolean;
  partnersVisible: boolean;
  partnerTierLayout: PartnerTierLayoutItem[];
  shopVisible: boolean;
  shopPlaceholderMessage?: string;
  shippingEnabled: boolean;
  pushOnOpponentGoal: boolean;
  // Metadata raw
  metadataJson?: string;
}

function configToValues(c: AdminTenantConfigDto): FormValues {
  return {
    primaryColor: c.primaryColor,
    secondaryColor: c.secondaryColor,
    accentColor: c.accentColor ?? undefined,
    logoUrl: c.logoUrl ?? undefined,
    clubName: c.clubName,
    appName: c.appName ?? undefined,
    clubCity: c.clubCity ?? undefined,
    clubStadium: c.clubStadium ?? undefined,
    clubFounded: c.clubFounded ?? undefined,
    defaultLocale: c.defaultLocale,
    timezone: c.timezone,
    minAppVersion: c.minAppVersion,
    subscriberDiscountPct: c.subscriberDiscountPct,
    subscriptionEndsAt: c.subscriptionEndsAt ? dayjs(c.subscriptionEndsAt) : null,
    hideNextMatch: c.hideNextMatch,
    partnersVisible: c.partnersVisible,
    // sourceTiers e' un dettaglio derivato del GET pubblico: la PATCH usa
    // forbidNonWhitelisted, quindi invia solo i due campi configurabili.
    partnerTierLayout: c.partnerTierLayout.map(({ tier, columns }) => ({ tier, columns })),
    shopVisible: c.shopVisible,
    shopPlaceholderMessage: c.shopPlaceholderMessage ?? undefined,
    shippingEnabled: c.shippingEnabled,
    pushOnOpponentGoal: c.pushOnOpponentGoal,
    metadataJson: c.metadata ? JSON.stringify(c.metadata, null, 2) : '{}',
  };
}

const hexFromEvent = (c: { toHexString: () => string } | string | null | undefined):
  | string
  | undefined => {
  if (!c) return undefined;
  return typeof c === 'string' ? c : c.toHexString();
};

/**
 * Configurazione tenant UNIFICATA (ex SettingsPage + TenantConfigPage, fuse).
 * Unico endpoint `PATCH /tenant/admin-config`, unico hook `useTenantAdminConfig`.
 * I tab usano `forceRender` così tutti i campi sono montati: evita la perdita
 * di valori dei tab mai aperti (stessa classe di bug del form prodotto).
 */
export function SettingsPage(): React.ReactElement {
  const [form] = Form.useForm<FormValues>();
  const { data: config, isLoading, refetch, isFetching } = useTenantAdminConfig();
  const updateMutation = useUpdateTenantConfig();

  // Antifrode — draft locale (non dentro il Form principale)
  const [antifraudDraft, setAntifraudDraft] = useState<AntifraudConfig>(ANTIFRAUD_BALANCED_DEFAULT);

  // Tassonomia shop — draft locale (non dentro il Form principale)
  const [shopTaxonomyDraft, setShopTaxonomyDraft] = useState<ShopTaxonomy>(SHOP_TAXONOMY_DEFAULT);

  // Manutenzione — purge immagini scontrini
  const [purgeRange, setPurgeRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [previewResult, setPreviewResult] = useState<PurgeImagesResult | null>(null);
  const purgeMutation = usePurgeReceiptImages();

  const onPurgePreview = async () => {
    if (!purgeRange) return;
    try {
      const result = await purgeMutation.mutateAsync({
        from: purgeRange[0].startOf('day').toISOString(),
        to: purgeRange[1].endOf('day').toISOString(),
        dryRun: true,
      });
      setPreviewResult(result);
    } catch {
      message.error('Errore anteprima');
    }
  };

  const onPurgeConfirm = async () => {
    if (!purgeRange) return;
    try {
      const result = await purgeMutation.mutateAsync({
        from: purgeRange[0].startOf('day').toISOString(),
        to: purgeRange[1].endOf('day').toISOString(),
        dryRun: false,
      });
      setPreviewResult(null);
      setPurgeRange(null);
      message.success(
        `Eliminazione completata: ${result.deleted} immagini rimosse, ${result.errors} errori.`,
      );
    } catch {
      message.error('Errore durante eliminazione');
    }
  };

  React.useEffect(() => {
    if (config) {
      form.setFieldsValue(configToValues(config));
      // Inizializza/aggiorna il draft antifrode dai dati server; se assente usa il default Bilanciato.
      setAntifraudDraft(config.antifraudConfig ?? ANTIFRAUD_BALANCED_DEFAULT);
      // Inizializza/aggiorna il draft tassonomia shop; normalizza null/parziali.
      setShopTaxonomyDraft(normalizeShopTaxonomy(config.shopTaxonomy));
    }
  }, [config, form]);

  const onSave = async (): Promise<void> => {
    // Valida la configurazione antifrode prima di inviare.
    const antifraudErrors = validateAntifraudConfig(antifraudDraft);
    if (antifraudErrors.length > 0) {
      message.error(`Antifrode — ${antifraudErrors[0]}`);
      return;
    }

    // Valida la tassonomia shop prima di inviare.
    const shopTaxonomyErrors = validateShopTaxonomy(shopTaxonomyDraft);
    if (shopTaxonomyErrors.length > 0) {
      message.error(`Shop / Catalogo — ${shopTaxonomyErrors[0]}`);
      return;
    }

    const v = await form.validateFields();
    let metadata: MetadataDraft | undefined;
    if (v.metadataJson && v.metadataJson.trim()) {
      try {
        const parsed: unknown = JSON.parse(v.metadataJson);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          throw new Error('Atteso oggetto JSON');
        }
        metadata = parsed as MetadataDraft;
      } catch (e) {
        message.error(`Metadata JSON non valido: ${(e as Error).message}`);
        return;
      }
    }
    const input: UpdateConfigInput = {
      primaryColor: v.primaryColor,
      secondaryColor: v.secondaryColor,
      accentColor: v.accentColor || null,
      logoUrl: v.logoUrl || null,
      clubName: v.clubName,
      appName: v.appName || null,
      clubCity: v.clubCity || null,
      clubStadium: v.clubStadium || null,
      clubFounded: v.clubFounded ?? null,
      subscriberDiscountPct: v.subscriberDiscountPct,
      subscriptionEndsAt: v.subscriptionEndsAt
        ? v.subscriptionEndsAt.endOf('day').toISOString()
        : null,
      hideNextMatch: v.hideNextMatch,
      partnersVisible: v.partnersVisible,
      partnerTierLayout: v.partnerTierLayout,
      shopVisible: v.shopVisible,
      shopPlaceholderMessage: v.shopPlaceholderMessage || null,
      shippingEnabled: v.shippingEnabled,
      pushOnOpponentGoal: v.pushOnOpponentGoal,
      minAppVersion: v.minAppVersion,
      defaultLocale: v.defaultLocale,
      timezone: v.timezone,
      metadata,
      antifraudConfig: antifraudDraft,
      shopTaxonomy: shopTaxonomyDraft,
    };
    try {
      await updateMutation.mutateAsync(input);
      message.success('Configurazione salvata — configVersion incrementata, l\'app ricaricherà la config.');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore salvataggio'));
    }
  };

  if (isLoading || !config) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }

  return (
    <>
      <PageHeader
        title="Configurazione tenant"
        subtitle="Branding, info club, check-in stadio, impostazioni app e shop. Ogni salvataggio incrementa configVersion: l'app mobile ricarica la config e ri-applica il branding."
        tags={
          <>
            <Tag>v{config.configVersion}</Tag>
            <Tag color="blue">{config.distribution}</Tag>
          </>
        }
        extra={
          <>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
              Ricarica
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={onSave}
              loading={updateMutation.isPending}
            >
              Salva
            </Button>
          </>
        }
      />

      <Card>
        <Form form={form} layout="vertical">
          <Tabs
            defaultActiveKey="branding"
            items={[
              {
                key: 'branding',
                label: 'Branding',
                forceRender: true,
                children: (
                  <>
                    <Space.Compact style={{ width: '100%' }}>
                      <Form.Item
                        name="primaryColor"
                        label="Colore primario"
                        rules={[{ required: true }]}
                        style={{ flex: 1, marginRight: 8 }}
                        getValueFromEvent={hexFromEvent}
                      >
                        <ColorPicker showText format="hex" />
                      </Form.Item>
                      <Form.Item
                        name="secondaryColor"
                        label="Colore secondario"
                        rules={[{ required: true }]}
                        style={{ flex: 1, marginRight: 8 }}
                        getValueFromEvent={hexFromEvent}
                      >
                        <ColorPicker showText format="hex" />
                      </Form.Item>
                      <Form.Item
                        name="accentColor"
                        label="Colore accent (opzionale)"
                        style={{ flex: 1 }}
                        getValueFromEvent={hexFromEvent}
                      >
                        <ColorPicker showText format="hex" allowClear />
                      </Form.Item>
                    </Space.Compact>
                    <Form.Item name="logoUrl" label="URL logo">
                      <Input placeholder="https://..." allowClear />
                    </Form.Item>
                    <Form.Item shouldUpdate noStyle>
                      {() => {
                        const url = form.getFieldValue('logoUrl');
                        if (!url) return null;
                        return (
                          <div style={{ marginBottom: 16 }}>
                            <img
                              src={url}
                              alt="logo preview"
                              style={{ maxHeight: 64, objectFit: 'contain' }}
                            />
                          </div>
                        );
                      }}
                    </Form.Item>
                  </>
                ),
              },
              {
                // T-255 — le notifiche della partita hanno una scheda propria:
                // prima l'interruttore del gol avversario era sepolto in "App &
                // Shop" dopo "Mostra sezione Partner" e l'admin non lo trovava.
                key: 'match-notifications',
                label: 'Notifiche partita',
                forceRender: true,
                children: (
                  <>
                    <Typography.Paragraph type="secondary" style={{ marginTop: 4 }}>
                      Notifiche push legate alla diretta. L&apos;interruttore &quot;notifica
                      gol&quot; della singola partita resta nella Dashboard Live: qui decidi solo
                      se, quando quella è accesa, la notifica parte ANCHE sul gol dell&apos;avversario.
                    </Typography.Paragraph>
                    <Form.Item
                      name="pushOnOpponentGoal"
                      label="Notifica anche sul gol dell'avversario"
                      valuePropName="checked"
                      tooltip="Quando attivo, la notifica gol parte anche quando segna l'avversario (testo neutro: 'Gol {avversario}' col punteggio). Vale solo se, sulla singola partita, la notifica gol è comunque accesa."
                    >
                      <Switch checkedChildren="Sì" unCheckedChildren="No" />
                    </Form.Item>
                  </>
                ),
              },
              {
                key: 'club',
                label: 'Info club',
                forceRender: true,
                children: (
                  <>
                    <Space.Compact style={{ width: '100%' }}>
                      <Form.Item
                        name="clubName"
                        label="Nome club"
                        rules={[{ required: true, max: 255 }]}
                        style={{ flex: 1, marginRight: 8 }}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item name="appName" label="Nome app" style={{ flex: 1 }}>
                        <Input />
                      </Form.Item>
                    </Space.Compact>
                    <Space.Compact style={{ width: '100%' }}>
                      <Form.Item name="clubCity" label="Città" style={{ flex: 1, marginRight: 8 }}>
                        <Input />
                      </Form.Item>
                      <Form.Item
                        name="clubStadium"
                        label="Stadio"
                        style={{ flex: 1, marginRight: 8 }}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item name="clubFounded" label="Anno fondazione" style={{ width: 160 }}>
                        <InputNumber min={1800} max={2100} precision={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Space.Compact>
                  </>
                ),
              },
              {
                key: 'app',
                label: 'App & Shop',
                forceRender: true,
                children: (
                  <>
                    <Space.Compact style={{ width: '100%' }}>
                      <Form.Item
                        name="defaultLocale"
                        label="Lingua default"
                        rules={[{ required: true }]}
                        style={{ width: 150, marginRight: 8 }}
                      >
                        <Select
                          options={[
                            { value: 'it', label: 'Italiano' },
                            { value: 'en', label: 'English' },
                          ]}
                        />
                      </Form.Item>
                      <Form.Item
                        name="timezone"
                        label="Timezone (IANA)"
                        rules={[{ required: true }]}
                        style={{ flex: 1, marginRight: 8 }}
                      >
                        <Input placeholder="Europe/Rome" />
                      </Form.Item>
                      <Form.Item
                        name="minAppVersion"
                        label="Min app version (semver)"
                        rules={[
                          { required: true },
                          { pattern: /^\d+\.\d+\.\d+/, message: 'Formato semver X.Y.Z' },
                        ]}
                        style={{ width: 180 }}
                      >
                        <Input placeholder="1.0.0" />
                      </Form.Item>
                    </Space.Compact>
                    <Space.Compact style={{ width: '100%' }}>
                      <Form.Item
                        name="subscriberDiscountPct"
                        label="Sconto abbonati (%)"
                        rules={[{ required: true, type: 'number', min: 0, max: 100 }]}
                        tooltip="DEPRECATO: non ha piu effetto. Lo sconto abbonati e ora una regola in Sconti store, dove puoi anche limitarlo per tier e per periodo."
                        style={{ width: 200, marginRight: 8 }}
                      >
                        <InputNumber min={0} max={100} precision={0} style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item
                        name="subscriptionEndsAt"
                        label="Scadenza abbonamento stagionale"
                        tooltip="Validità del titolo di ingresso digitale per i tifosi con abbonamento caricato"
                        style={{ flex: 1 }}
                      >
                        <DatePicker
                          style={{ width: '100%' }}
                          format="DD/MM/YYYY"
                          placeholder="Es. 30/06/2026"
                          disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
                          allowClear
                        />
                      </Form.Item>
                    </Space.Compact>
                    <Form.Item
                      name="hideNextMatch"
                      label="Nascondi prossima partita (pausa estiva)"
                      valuePropName="checked"
                      tooltip="Quando attivo, la Home App mostra lo slider banner invece della card prossima partita."
                    >
                      <Switch checkedChildren="Sì" unCheckedChildren="No" />
                    </Form.Item>
                    <Form.Item
                      name="partnersVisible"
                      label="Mostra sezione Partner"
                      valuePropName="checked"
                      tooltip="Quando attivo, mostra lo slider Partner in Home e la pagina Partner con l'elenco completo."
                    >
                      <Switch checkedChildren="Sì" unCheckedChildren="No" />
                    </Form.Item>
                    <Form.Item
                      label="Griglia partner"
                      tooltip="Scegli quante card affiancare per ogni sezione della pagina Partner. I tier storici Premium, Media e Official restano compatibili e vengono raggruppati automaticamente."
                    >
                      <Form.List name="partnerTierLayout">
                        {(fields) => (
                          <Space direction="vertical" style={{ width: '100%' }} size={8}>
                            {fields.map((field) => (
                              <Space key={field.key} align="center" style={{ width: '100%' }}>
                                <Form.Item name={[field.name, 'tier']} noStyle>
                                  <Input readOnly style={{ width: 130 }} />
                                </Form.Item>
                                <Form.Item name={[field.name, 'columns']} noStyle>
                                  <Select
                                    style={{ width: 180 }}
                                    options={[
                                      { value: 2, label: '2 colonne' },
                                      { value: 3, label: '3 colonne' },
                                    ]}
                                  />
                                </Form.Item>
                              </Space>
                            ))}
                          </Space>
                        )}
                      </Form.List>
                    </Form.Item>
                    <Form.Item
                      name="shopVisible"
                      label="Mostra Shop"
                      valuePropName="checked"
                      tooltip="Quando disattivo, l'app mostra il messaggio placeholder qui sotto al posto del catalogo prodotti."
                    >
                      <Switch checkedChildren="Sì" unCheckedChildren="No" />
                    </Form.Item>
                    <Form.Item
                      name="shopPlaceholderMessage"
                      label="Messaggio placeholder Shop"
                      tooltip="Mostrato al posto del catalogo quando 'Mostra Shop' è disattivo."
                      rules={[{ max: 255, message: 'Massimo 255 caratteri' }]}
                    >
                      <Input placeholder="Stiamo allestendo!" allowClear />
                    </Form.Item>
                    <Form.Item
                      name="shippingEnabled"
                      label="Spedizione attiva"
                      valuePropName="checked"
                      tooltip="Quando disattiva, il checkout accetta solo il ritiro in store e rifiuta gli ordini con spedizione. Riattivabile in qualsiasi momento."
                    >
                      <Switch checkedChildren="Sì" unCheckedChildren="Solo ritiro" />
                    </Form.Item>
                    {/* T-116: URL regolamento loyalty + gestione fasi spostati
                        nella pagina "Periodi di raccolta" (menu Loyalty). */}
                  </>
                ),
              },
              {
                key: 'antifraud',
                label: 'Antifrode',
                forceRender: true,
                children: (
                  <AntifraudSettingsTab
                    initialValue={config?.antifraudConfig ?? null}
                    onChange={setAntifraudDraft}
                  />
                ),
              },
              {
                key: 'shop-taxonomy',
                label: 'Shop / Catalogo',
                forceRender: true,
                children: (
                  <ShopTaxonomyTab
                    initialValue={config?.shopTaxonomy ?? null}
                    onChange={setShopTaxonomyDraft}
                  />
                ),
              },
              {
                key: 'maintenance',
                label: 'Manutenzione',
                forceRender: false,
                children: (
                  <Space direction="vertical" style={{ width: '100%' }} size={16}>
                    <Alert
                      type="info"
                      showIcon
                      message="Gestione spazio Repository Media"
                      description="Le immagini degli scontrini già validati possono essere eliminate per liberare spazio. I dati dello scontrino, i punti assegnati e la cronologia rimangono intatti nel database e visibili all'utente."
                    />
                    <Alert
                      type="warning"
                      showIcon
                      message="Operazione irreversibile"
                      description="Una volta eliminate, le immagini non possono essere recuperate. Vengono eliminate solo le immagini reali caricate dai tifosi; gli scontrini inseriti manualmente non sono interessati."
                    />
                    <Card size="small" title="Elimina immagini scontrini per range di date">
                      <Space direction="vertical" style={{ width: '100%' }} size={12}>
                        <Typography.Text type="secondary">
                          Seleziona il periodo di competenza (data caricamento scontrino).
                        </Typography.Text>
                        <Space wrap>
                          <DatePicker.RangePicker
                            value={purgeRange}
                            format="DD/MM/YYYY"
                            onChange={(v) => {
                              setPurgeRange(v as [Dayjs, Dayjs] | null);
                              setPreviewResult(null);
                            }}
                            disabledDate={(d) => d.isAfter(dayjs())}
                          />
                          <Button
                            icon={<ClearOutlined />}
                            onClick={onPurgePreview}
                            disabled={!purgeRange}
                            loading={purgeMutation.isPending}
                          >
                            Anteprima
                          </Button>
                        </Space>
                        {previewResult !== null && (
                          <Alert
                            type={previewResult.count === 0 ? 'success' : 'warning'}
                            showIcon
                            message={
                              previewResult.count === 0
                                ? 'Nessuna immagine da eliminare nel periodo selezionato.'
                                : `${previewResult.count} immagini verranno eliminate definitivamente dal Repository Media.`
                            }
                            action={
                              previewResult.count > 0 && (
                                <Popconfirm
                                  title="Elimina immagini"
                                  description={`Stai per eliminare ${previewResult.count} immagini. I punti e i dati degli scontrini rimarranno invariati. Continuare?`}
                                  okText="Sì, elimina"
                                  cancelText="Annulla"
                                  okButtonProps={{ danger: true }}
                                  onConfirm={onPurgeConfirm}
                                >
                                  <Button danger loading={purgeMutation.isPending}>
                                    Elimina ora
                                  </Button>
                                </Popconfirm>
                              )
                            }
                          />
                        )}
                      </Space>
                    </Card>
                  </Space>
                ),
              },
              {
                key: 'metadata',
                label: 'Metadata',
                forceRender: true,
                children: (
                  <>
                    <Alert
                      message="JSON free-form"
                      description="Esempio: storeUrl.ios, storeUrl.android, forceUpdateBelow. Letti da /v1/app/version-check."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                    <Form.Item name="metadataJson" label="Metadata (JSON)">
                      <Input.TextArea rows={12} style={{ fontFamily: 'monospace', fontSize: 12 }} />
                    </Form.Item>
                    <Descriptions column={2} size="small" bordered title="Info tecniche (sola lettura)">
                      <Descriptions.Item label="Tenant ID">{config.tenantId}</Descriptions.Item>
                      <Descriptions.Item label="Distribuzione">{config.distribution}</Descriptions.Item>
                      <Descriptions.Item label="Ultima modifica">
                        {config.updatedAt
                          ? new Date(config.updatedAt).toLocaleString('it-IT')
                          : '—'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Config version">
                        {config.configVersion}
                      </Descriptions.Item>
                    </Descriptions>
                  </>
                ),
              },
            ]}
          />
        </Form>
      </Card>
    </>
  );
}
